import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { TrackingEvents, sendLoggingEvents } from '../../events';
import {
  currentConversationAtom,
  currentConversationMessagesAtom,
  currentConversationParticipantNamesAtom,
  currentConversationSidAtom,
  isChatErrorAtom,
} from '../../session/state';
import { useGetClientSafetyGuard } from '../../useGetClientSafetyGuard';
import { connectedAtAtom } from '../../video-sdk';
import { twilioIdentityAtom } from '../../video-sdk/twilio';
import {
  ProviderDrawerState,
  currentConversationPatientShortIdAtom,
  drawerStateAtom,
  numWaitingVisitorsAtom,
  sessionConversationReadyAtom,
} from '../state';
import { ChatDrawer } from './ChatDrawer';
import { SAFETY_GUARD_BANNER_TEXT } from './constants';

export function ChatDrawerWrapper() {
  const [drawerState, setDrawerState] = useAtom(drawerStateAtom);
  const numWaitingVisitors = useAtomValue(numWaitingVisitorsAtom);
  const sessionConversationReady = useAtomValue(sessionConversationReadyAtom);
  const numberOfClientChats = sessionConversationReady ? numWaitingVisitors + 1 : numWaitingVisitors;
  const connectedAt = useAtomValue(connectedAtAtom);
  const conversation = useAtomValue(currentConversationAtom);
  const messages = useAtomValue(currentConversationMessagesAtom);
  const identity = useAtomValue(twilioIdentityAtom);
  const patientShortId = useAtomValue(currentConversationPatientShortIdAtom);
  const { requiresSafetyGuards } = useGetClientSafetyGuard(patientShortId);
  const chatName = useAtomValue(currentConversationParticipantNamesAtom).join(', ');
  const isChatError = useAtomValue(isChatErrorAtom);
  const setCurrentConversationSid = useSetAtom(currentConversationSidAtom);
  const sendMessage = (message: string) => {
    sendLoggingEvents(TrackingEvents.ANY_MESSAGE_SEND);
    if (drawerState === ProviderDrawerState.WAITING_ROOM_CHAT) {
      sendLoggingEvents(TrackingEvents.ANY_WAITING_ROOM_MESSAGE_SEND);
    }
    if (drawerState === ProviderDrawerState.SESSION_CHAT) {
      sendLoggingEvents(TrackingEvents.ANY_SESSION_MESSAGE_SEND);
    }
    conversation?.sendMessage(message);
  };

  const onGoToChatList = () => {
    setDrawerState(ProviderDrawerState.ALL_CLIENTS_CHAT_LIST);
    setCurrentConversationSid(undefined);
  };

  return (
    <ChatDrawer
      onGoToChatList={onGoToChatList}
      onSend={sendMessage}
      isError={isChatError}
      warningBannerText={requiresSafetyGuards ? SAFETY_GUARD_BANNER_TEXT : undefined}
      isEmptyState={numWaitingVisitors === 0 && drawerState === ProviderDrawerState.WAITING_ROOM_CHAT}
      {...{
        chatName,
        connectedAt,
        drawerState,
        identity,
        messages,
        numberOfClientChats,
      }}
    />
  );
}
